import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Slider from "react-slick";
import "./Home.scss";
import { useTranslation } from "react-i18next";
import DiscountPopup from "./../../components/Discount/DiscountPopup";

const Home = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const base = `/${lang || "en"}`;

  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch Vimeo videos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(
          "https://api.vimeo.com/users/217327133/videos",
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_VIMEO_ACCESS_TOKEN}`,
            },
          },
        );

        const weddingVideos = response.data.data.filter((video) =>
          video.tags.some((tag) => tag.name.toLowerCase() === "wedding"),
        );

        setVideos(weddingVideos);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching videos:", error);
      }
    };

    fetchVideos();
  }, []);

  // Slider settings for the carousel
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          dots: true,
          vertical: true,
          verticalSwiping: true,
          arrows: false,
          appendDots: (dots) => (
            <div className="vertical-dots">
              <ul>{dots}</ul>
            </div>
          ),
        },
      },
    ],
  };

  return (
    <>
     <DiscountPopup />
      <main className="home">
        {/* Hero Section */}
        <section className="hero">
          <video autoPlay loop muted className="hero__video" playsInline>
            <source src="/videos/teaser.mp4" type="video/mp4" />
          </video>
          <div
            className="hero__image"
            style={{
              background:
                'url("/Images/one.jpg") center center / cover no-repeat',
            }}
          ></div>
          <div className="hero__content">
            <h1 className="hero__title">{t("home.hero.title")}</h1>
            <p className="hero__subtitle">{t("home.hero.subtitle")}</p>
          </div>
        </section>

        {/* About Section */}
        <section className="about">
          <h2 className="about__title">{t("home.about.title")}</h2>
          <div className="about__container">
            <div className="about__box-one">
              <p className="about__text">
                {t("home.about.paragraph1", { company: "Moss Media" })}
              </p>
              <img
                src="/Images/three.jpg"
                alt="Memorable scene"
                className="about__image"
              />
            </div>

            <div className="about__box-two">
              <p className="about__text">
                {t("home.about.paragraph2", { company: "Moss Media" })}
              </p>

              <img
                src="/images/five.jpg"
                alt="Elegant moment"
                className="about__image"
              />
            </div>

            <div className="about__box-three">
              <p className="about__text">
                {t("home.about.paragraph3", { company: "Moss Media" })}
              </p>

              <img
                src="/images/six.jpg"
                alt="Elegant moment"
                className="about__image about__image--third"
              />
            </div>
          </div>
        </section>

        {/* Videos Section */}
        <section className="videos">
          <div className="videos__container">
            <h2 className="videos__title">{t("home.videos.title")}</h2>
          </div>
          <div>
            {isLoading ? (
              <div className="videos__loading">{t("home.videos.loading")}</div>
            ) : videos.length === 0 ? (
              <div className="videos__empty">{t("home.videos.empty")}</div>
            ) : (
              <>
                <Slider {...settings} className="videos__carousel">
                  {videos.map((video) => (
                    <div key={video.uri} className="videos__item">
                      <iframe
                        src={`https://player.vimeo.com/video/${
                          video.uri.split("/")[2]
                        }`}
                        title={video.name}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                      <p className="videos__caption">{video.name}</p>
                    </div>
                  ))}
                </Slider>
                <Link to={`${base}/portfolio`}>
                  <button className="videos__cta">
                    {t("home.videos.cta")}
                  </button>
                </Link>
              </>
            )}
          </div>
        </section>

        {/* Expect Section */}
        <section className="expect">
          <h2 className="expect__title">{t("home.expect.title")}</h2>
          <div className="expect__features">
            <div className="expect__top">
              {t("home.expect.features", { returnObjects: true })
                .slice(0, 3)
                .map((feature, index) => (
                  <div className="expect__feature" key={index}>
                    <h3 className="expect__heading">{feature.heading}</h3>
                    <img
                      src={feature.image}
                      alt={feature.heading}
                      className="expect__image"
                    />
                    <p className="expect__text">{feature.text}</p>
                  </div>
                ))}
            </div>
            <div className="expect__bottom">
              {t("home.expect.features", { returnObjects: true })
                .slice(3, 6)
                .map((feature, index) => (
                  <div className="expect__feature" key={index}>
                    <h3 className="expect__heading">{feature.heading}</h3>
                    <img
                      src={feature.image}
                      alt={feature.heading}
                      className="expect__image"
                    />
                    <p className="expect__text">{feature.text}</p>
                  </div>
                ))}
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="packages">
          {["diamond", "gold", "silver", "pearl"].map((pkg) => (
            <div
              key={pkg}
              className={`packages__card packages__card--${pkg}`}
              style={{
                backgroundImage: `url("${
                  {
                    diamond: "/Images/diamond.png",
                    gold: "/Images/gold2.jpg",
                    silver: "/Images/silver3.jpg",
                    pearl: "/Images/pearl5.png",
                  }[pkg]
                }")`,
              }}
            >
              <Link to={`${base}/services`} className="packages__link">
                <h3 className="packages__title">
                  {t(`home.packages.${pkg}.title`)}
                </h3>

                <h1 className="packages__headline">
                  {t(`home.packages.${pkg}.headline`)}
                </h1>

                <p className="packages__description">
                  {t(`home.packages.${pkg}.desc1`)}
                </p>

                {i18n.exists(`home.packages.${pkg}.desc2`) && (
                  <p className="packages__description">
                    {t(`home.packages.${pkg}.desc2`)}
                  </p>
                )}
              </Link>
            </div>
          ))}
        </section>
      </main>
    </>
  );
};

export default Home;
